import { useState, useEffect } from "react";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Textarea } from "../components/ui/textarea";
import { Upload, BookOpen, Ticket, Mail } from "lucide-react";

const PRODUCTS = [
  { lk: "correspondance_simple", label: "Correspondance simple (35€)" },
  { lk: "correspondance_coffret", label: "Correspondance + coffret (59€)" },
  { lk: "programme_seul", label: "Programme Essentiel (102€)" },
  { lk: "programme_coffret", label: "Programme Épistolaire (152€)" },
];

const PROGRAMME_TYPES = [
  { value: "essentiel", label: "Programme Essentiel (102€)" },
  { value: "epistolaire", label: "Programme Épistolaire (152€)" },
];

export default function AdminImport() {
  const [participants, setParticipants] = useState([]);
  const [prog, setProg] = useState({ email: "", name: "", current_week: 0, programme_type: "essentiel" });
  const [code, setCode] = useState({ email: "", name: "", lookup_key: "correspondance_simple", months_validity: 12 });
  const [nl, setNl] = useState("");
  const [issuedCode, setIssuedCode] = useState(null);

  useEffect(() => { api.get("/admin/import/programme").then(r => setParticipants(r.data.items)); }, []);

  async function importProg(e) {
    e.preventDefault();
    try {
      await api.post("/admin/import/programme", { ...prog, current_week: parseInt(prog.current_week, 10) });
      toast.success(`${prog.name} ajoutée au ${prog.programme_type === "epistolaire" ? "Programme Épistolaire" : "Programme Essentiel"}, semaine ${prog.current_week}`);
      setProg({ email: "", name: "", current_week: 0, programme_type: "essentiel" });
      const r = await api.get("/admin/import/programme");
      setParticipants(r.data.items);
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  }

  async function importCode(e) {
    e.preventDefault();
    try {
      const r = await api.post("/admin/import/correspondance-code", { ...code, months_validity: parseInt(code.months_validity, 10) });
      setIssuedCode(r.data.code);
      toast.success(`Code ${r.data.code} généré`);
      setCode({ email: "", name: "", lookup_key: "correspondance_simple", months_validity: 12 });
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  }

  async function importNl(e) {
    e.preventDefault();
    const emails = nl.split(/[\n,;]/).map(s => s.trim()).filter(Boolean);
    if (!emails.length) return toast.error("Aucun email à importer");
    try {
      const r = await api.post("/admin/import/newsletter", { emails });
      toast.success(`${r.data.added} ajoutés, ${r.data.skipped} ignorés (doublons ou invalides)`);
      setNl("");
    } catch (e) { toast.error(e.response?.data?.detail || "Erreur"); }
  }

  return (
    <div data-testid="admin-import" className="max-w-4xl">
      <p className="font-hand text-xl text-[#A3876A] mb-2">Reprendre où vous en étiez</p>
      <h1 className="font-serif text-4xl mb-10">Intégrer vos clientes existantes</h1>

      {/* PROGRAMME */}
      <section className="bg-[#F5F0E6] p-8 border border-[#E8DCC8] mb-10">
        <div className="flex items-start gap-4 mb-6">
          <BookOpen className="text-[#A3876A] mt-1" size={22}/>
          <div>
            <h2 className="font-serif text-2xl">Programme d'écriture — reprendre à la bonne semaine</h2>
            <p className="text-sm text-[#7C7064] mt-1">Indiquez à quelle semaine la personne en est. Elle recevra la suite à partir de la semaine suivante (dès que l'envoi automatique sera actif).</p>
          </div>
        </div>
        <form onSubmit={importProg} className="grid md:grid-cols-2 gap-4">
          <input data-testid="imp-prog-name" className="field-input" placeholder="Prénom" required value={prog.name} onChange={e=>setProg({...prog, name:e.target.value})}/>
          <input data-testid="imp-prog-email" className="field-input" type="email" placeholder="Email" required value={prog.email} onChange={e=>setProg({...prog, email:e.target.value})}/>
          <select data-testid="imp-prog-type" value={prog.programme_type} onChange={e=>setProg({...prog, programme_type:e.target.value})}
            className="w-full bg-transparent border-b border-[#E8DCC8] py-2 outline-none">
            {PROGRAMME_TYPES.map(p => <option key={p.value} value={p.value}>{p.label}</option>)}
          </select>
          <input data-testid="imp-prog-week" className="field-input" type="number" min="0" max="52" placeholder="Semaine actuelle (0-52)" required value={prog.current_week} onChange={e=>setProg({...prog, current_week:e.target.value})}/>
          <button data-testid="imp-prog-submit" className="btn-primary md:col-span-2 justify-center"><Upload size={14}/> Ajouter au programme</button>
        </form>
        {participants.length > 0 && (
          <div className="mt-6 bg-[#FDFBF7] border border-[#E8DCC8]">
            <table className="w-full text-sm">
              <thead className="bg-[#F5F0E6] text-left"><tr>
                <th className="p-3">Prénom</th><th className="p-3">Email</th><th className="p-3">Programme</th><th className="p-3">Semaine</th><th className="p-3">Source</th><th className="p-3">Ajoutée le</th>
              </tr></thead>
              <tbody>
                {participants.map(p => (
                  <tr key={p.email} className="border-t border-[#E8DCC8]">
                    <td className="p-3">{p.customer_name}</td>
                    <td className="p-3 text-xs text-[#7C7064]">{p.email}</td>
                    <td className="p-3 text-xs">{p.programme_type === "epistolaire" ? "Épistolaire" : "Essentiel"}</td>
                    <td className="p-3">{p.current_week} / 52</td>
                    <td className="p-3 text-xs text-[#7C7064]">{p.source}</td>
                    <td className="p-3 text-xs text-[#7C7064]">{new Date(p.created_at).toLocaleDateString("fr-FR")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* CORRESPONDANCE CODE */}
      <section className="bg-[#F5F0E6] p-8 border border-[#E8DCC8] mb-10">
        <div className="flex items-start gap-4 mb-6">
          <Ticket className="text-[#A3876A] mt-1" size={22}/>
          <div>
            <h2 className="font-serif text-2xl">Correspondance — offrir un code de rattrapage</h2>
            <p className="text-sm text-[#7C7064] mt-1">Générez un code (préfixe LEGACY-) que la personne pourra saisir sur le site pour récupérer sa correspondance sans payer.</p>
          </div>
        </div>
        <form onSubmit={importCode} className="grid md:grid-cols-2 gap-4">
          <input data-testid="imp-code-name" className="field-input" placeholder="Prénom" required value={code.name} onChange={e=>setCode({...code, name:e.target.value})}/>
          <input data-testid="imp-code-email" className="field-input" type="email" placeholder="Email" required value={code.email} onChange={e=>setCode({...code, email:e.target.value})}/>
          <select data-testid="imp-code-product" value={code.lookup_key} onChange={e=>setCode({...code, lookup_key:e.target.value})}
            className="w-full bg-transparent border-b border-[#E8DCC8] py-2 outline-none">
            {PRODUCTS.map(p => <option key={p.lk} value={p.lk}>{p.label}</option>)}
          </select>
          <input data-testid="imp-code-months" className="field-input" type="number" min="1" max="24" placeholder="Validité (mois)" required value={code.months_validity} onChange={e=>setCode({...code, months_validity:e.target.value})}/>
          <button data-testid="imp-code-submit" className="btn-primary md:col-span-2 justify-center"><Upload size={14}/> Générer le code</button>
        </form>
        {issuedCode && (
          <div className="mt-6 bg-[#FDFBF7] p-4 border border-[#A3876A]">
            <p className="text-sm text-[#7C7064] mb-1">Code généré (à transmettre à la cliente) :</p>
            <p className="font-mono text-2xl tracking-widest text-[#2C2621]">{issuedCode}</p>
            <p className="text-xs text-[#7C7064] mt-2">Elle le saisira sur la page correspondance pour récupérer son cadeau sans payer.</p>
          </div>
        )}
      </section>

      {/* NEWSLETTER */}
      <section className="bg-[#F5F0E6] p-8 border border-[#E8DCC8]">
        <div className="flex items-start gap-4 mb-6">
          <Mail className="text-[#A3876A] mt-1" size={22}/>
          <div>
            <h2 className="font-serif text-2xl">Newsletter — importer en masse</h2>
            <p className="text-sm text-[#7C7064] mt-1">Collez vos emails, un par ligne (ou séparés par virgule). Aucun email de bienvenue n'est envoyé lors de l'import.</p>
          </div>
        </div>
        <form onSubmit={importNl}>
          <Textarea data-testid="imp-nl-emails" rows={8} value={nl} onChange={e=>setNl(e.target.value)}
            placeholder={"marie@exemple.fr\nsophie@exemple.fr\njeanne@exemple.fr"}
            className="bg-white border border-[#E8DCC8] font-mono text-sm"/>
          <button data-testid="imp-nl-submit" className="btn-primary mt-4"><Upload size={14}/> Importer</button>
        </form>
      </section>
    </div>
  );
}
